import type { ManagedUser } from '@/api/users'
import { Role } from '@/auth/roles'

type Named = Pick<ManagedUser, 'firstName' | 'lastName' | 'email'>

export function fullName(user: Named): string {
  return [user.firstName, user.lastName].filter(Boolean).join(' ')
}

/**
 * The best label available, in the order a reader recognises the person by. An account can have
 * neither — an invitation that has not been used carries whatever the administrator typed, and
 * that is not guaranteed either — hence the caller-supplied last resort.
 */
export function userLabel(user: Named, fallback: string): string {
  return fullName(user) || user.email || fallback
}

export function initials(user: Named): string {
  return (
    [user.firstName?.[0], user.lastName?.[0]].filter(Boolean).join('').toUpperCase() ||
    user.email?.[0]?.toUpperCase() ||
    '?'
  )
}

/**
 * The API sends roles `ROLE_`-prefixed, the UI thinks in bare names. Anything the frontend does
 * not know about becomes `null` rather than being rendered raw — the same rule the backend
 * applies to roles it does not know.
 */
export function bareRole(role: string | null): Role | null {
  const bare = role?.replace(/^ROLE_/, '')
  return bare === Role.ADMIN || bare === Role.SALES ? bare : null
}
